/* ==========================================================================
   Lampenheld – global.js
   Scroll-Animationen (hoch & runter), Parallax, Header, Mega-Menü,
   Mobile-Menü, Marquee, Karussell, AJAX-Warenkorb.
   ========================================================================== */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll-Zustand (Richtung + Geschwindigkeit) ---------- */
  const scrollState = { y: window.scrollY, lastY: window.scrollY, dir: 1, velocity: 0 };

  /* ---------- 1. Reveal: bidirektional ---------- */
  function splitWords(el) {
    if (el.dataset.splitDone) return;
    const text = el.textContent.trim().split(/\s+/);
    el.setAttribute('aria-label', el.textContent.trim());
    el.innerHTML = text
      .map((w, i) => `<span class="w" aria-hidden="true"><span style="--i:${i}">${w}</span></span>`)
      .join(' ');
    el.dataset.splitDone = '1';
  }

  // Ziel -> Elemente. Masken-Elemente (clip-path) werden über ihr Elternelement beobachtet,
  // weil ein komplett geclipptes Element nie als "sichtbar" gemeldet wird.
  const revealTargets = new WeakMap();
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          (revealTargets.get(entry.target) || [entry.target]).forEach((el) => {
            if (entry.isIntersecting) {
              el.classList.add('is-visible');
            } else {
              el.classList.remove('is-visible');
              // Liegt das Element oberhalb des Viewports? -> beim Hochscrollen von oben einfliegen
              el.classList.toggle('is-above', entry.boundingClientRect.top < 0);
            }
          });
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' })
    : null;

  function initReveal(root = document) {
    root.querySelectorAll('.split-words').forEach(splitWords);
    root.querySelectorAll('[data-reveal-stagger]').forEach((group) => {
      const step = parseInt(group.dataset.revealStagger, 10) || 90;
      group.querySelectorAll(':scope > [data-reveal], :scope > * > [data-reveal]').forEach((child, i) => {
        child.style.setProperty('--reveal-delay', `${(i % 8) * step}ms`);
      });
    });
    root.querySelectorAll('[data-reveal]').forEach((el) => {
      if (reduceMotion || !revealObserver) { el.classList.add('is-visible'); return; }
      const target = el.dataset.reveal === 'mask' ? el.parentElement : el;
      const list = revealTargets.get(target) || [];
      if (!list.includes(el)) list.push(el);
      revealTargets.set(target, list);
      revealObserver.observe(target);
    });
  }

  /* ---------- 2. Parallax + Scroll-Fade (rAF) ---------- */
  let parallaxEls = [];
  let fadeEls = [];
  let hscrollEls = [];
  function collectScrollEls() {
    parallaxEls = [...document.querySelectorAll('[data-parallax]')];
    fadeEls = [...document.querySelectorAll('[data-scroll-fade]')];
    hscrollEls = [...document.querySelectorAll('[data-hscroll]')];
    layoutHscroll();
  }

  // Höhe der gepinnten Sektion = Viewport + horizontale Strecke
  function layoutHscroll() {
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    hscrollEls.forEach((sec) => {
      const track = sec.querySelector('[data-hscroll-track]');
      if (!track) return;
      const vp = track.parentElement;
      if (!sec._bound) {
        sec._bound = true;
        vp.addEventListener('scroll', () => window.requestAnimationFrame(onScrollFrame), { passive: true });
      }
      if (!desktop || reduceMotion) {
        sec.classList.remove('is-pinned');
        sec.style.height = '';
        track.style.transform = '';
        return;
      }
      sec.classList.add('is-pinned');
      const padLeft = parseFloat(getComputedStyle(vp).paddingLeft) || 0;
      sec._travel = Math.max(track.scrollWidth + padLeft * 2 - window.innerWidth, 0);
      sec.style.height = `${window.innerHeight + sec._travel}px`;
    });
  }
  window.addEventListener('resize', () => layoutHscroll());
  window.addEventListener('load', () => layoutHscroll());

  const progressBar = document.querySelector('.scroll-progress');
  let ticking = false;

  function onScrollFrame() {
    const vh = window.innerHeight;
    const y = window.scrollY;
    scrollState.velocity = y - scrollState.lastY;
    if (Math.abs(scrollState.velocity) > 1) scrollState.dir = scrollState.velocity > 0 ? 1 : -1;
    scrollState.lastY = y;

    if (!reduceMotion) {
      parallaxEls.forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        const speed = parseFloat(el.dataset.parallax) || 0.2;
        const offset = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      });
      fadeEls.forEach((el) => {
        const p = Math.min(Math.max(y / (vh * 0.8), 0), 1);
        el.style.opacity = (1 - p * 0.9).toFixed(3);
        el.style.transform = `translate3d(0, ${(p * 80).toFixed(1)}px, 0) scale(${(1 - p * 0.06).toFixed(3)})`;
      });
    }

    // Horizontal-Scroll-Sektionen (pinned)
    hscrollEls.forEach((sec) => {
      const track = sec.querySelector('[data-hscroll-track]');
      const bar = sec.querySelector('[data-hscroll-bar]');
      if (!track) return;
      if (!sec.classList.contains('is-pinned')) {
        if (bar) {
          const vp = track.parentElement;
          const max = vp.scrollWidth - vp.clientWidth;
          bar.parentElement.style.setProperty('--hp', max > 0 ? (vp.scrollLeft / max).toFixed(3) : 0);
        }
        return;
      }
      const r = sec.getBoundingClientRect();
      const dist = sec.offsetHeight - vh;
      const p = Math.min(Math.max(-r.top / dist, 0), 1);
      const travel = sec._travel || 0;
      track.style.transform = `translate3d(${(-p * travel).toFixed(1)}px,0,0)`;
      if (bar) bar.parentElement.style.setProperty('--hp', p.toFixed(3));
    });

    if (progressBar) {
      const max = document.documentElement.scrollHeight - vh;
      progressBar.style.setProperty('--progress', max > 0 ? (y / max).toFixed(4) : 0);
    }

    updateHeader(y);
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) { window.requestAnimationFrame(onScrollFrame); ticking = true; }
  }, { passive: true });
  window.addEventListener('resize', () => window.requestAnimationFrame(onScrollFrame));

  /* ---------- 3. Header: beim Runterscrollen ausblenden, beim Hochscrollen zeigen ---------- */
  let headerEl = null;
  let lastHeaderY = 0;
  function updateHeader(y) {
    if (!headerEl) return;
    headerEl.classList.toggle('is-scrolled', y > 10);
    if (headerEl.dataset.sticky !== 'smart' || headerEl.querySelector('.mega.is-open') || document.body.classList.contains('menu-open')) {
      lastHeaderY = y; return;
    }
    if (y > lastHeaderY + 6 && y > 220) headerEl.classList.add('is-hidden');
    else if (y < lastHeaderY - 6 || y < 220) headerEl.classList.remove('is-hidden');
    lastHeaderY = y;
  }

  /* ---------- 4. Marquee: Geschwindigkeit & Richtung folgen dem Scrollen ---------- */
  function initMarquees() {
    document.querySelectorAll('[data-marquee]').forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = '1';
      const track = wrap.querySelector('.marquee__track');
      if (!track || reduceMotion) return;
      const base = parseFloat(wrap.dataset.marquee) || 0.6;
      let x = 0;
      let boost = 0;
      const loop = () => {
        const half = track.scrollWidth / 2;
        boost += (Math.abs(scrollState.velocity) * 0.35 - boost) * 0.08;
        x -= (base + boost) * scrollState.dir;
        if (x <= -half) x += half;
        if (x > 0) x -= half;
        track.style.transform = `translate3d(${x.toFixed(2)}px,0,0)`;
        scrollState.velocity *= 0.9;
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
    });
  }

  /* ---------- 5. Mega-Menü ---------- */
  function initMegaMenu(root = document) {
    const header = root.querySelector('[data-header]');
    if (!header) return;
    headerEl = header;
    if (header.dataset.ready) return;
    header.dataset.ready = '1';
    const items = header.querySelectorAll('[data-mega-item]');
    let closeTimer;

    const closeAll = (except) => {
      items.forEach((item) => {
        if (item === except) return;
        item.classList.remove('is-open');
        item.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'false');
      });
      header.classList.toggle('has-mega-open', !!except);
    };
    const open = (item) => {
      clearTimeout(closeTimer);
      closeAll(item);
      item.classList.add('is-open');
      item.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'true');
      header.classList.add('has-mega-open');
    };

    items.forEach((item) => {
      const trigger = item.querySelector('[data-mega-trigger]');
      item.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) open(item); });
      item.addEventListener('mouseleave', () => { closeTimer = setTimeout(() => closeAll(), 160); });
      trigger?.addEventListener('click', (e) => {
        if (item.classList.contains('is-open') && !window.matchMedia('(hover: hover)').matches) return;
        if (!item.classList.contains('is-open')) { e.preventDefault(); open(item); }
      });
      trigger?.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === ' ') {
          e.preventDefault(); open(item);
          item.querySelector('.mega a')?.focus();
        }
      });
      // Panel-Tabs (linke Spalte -> rechte Inhalte)
      item.querySelectorAll('[data-mega-tab]').forEach((tab) => {
        const activate = () => {
          item.querySelectorAll('[data-mega-tab]').forEach((t) => t.classList.toggle('is-active', t === tab));
          item.querySelectorAll('[data-mega-pane]').forEach((p) => p.classList.toggle('is-active', p.dataset.megaPane === tab.dataset.megaTab));
        };
        tab.addEventListener('mouseenter', activate);
        tab.addEventListener('focus', activate);
      });
    });

    header.addEventListener('focusout', (e) => {
      if (!header.contains(e.relatedTarget)) closeAll();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const openItem = header.querySelector('[data-mega-item].is-open');
        if (openItem) { closeAll(); openItem.querySelector('[data-mega-trigger]')?.focus(); }
        closeDrawer();
      }
    });
    document.addEventListener('click', (e) => { if (!header.contains(e.target)) closeAll(); });

    // Mobile Drawer
    header.querySelector('[data-drawer-open]')?.addEventListener('click', openDrawer);
    document.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', closeDrawer));
    document.querySelectorAll('[data-drawer-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        panel.hidden = expanded;
      });
    });

    // Suche
    const searchToggle = header.querySelector('[data-search-toggle]');
    const searchBar = header.querySelector('[data-search-bar]');
    searchToggle?.addEventListener('click', () => {
      const isOpen = searchBar.classList.toggle('is-open');
      searchToggle.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) setTimeout(() => searchBar.querySelector('input')?.focus(), 50);
    });
  }

  function openDrawer() {
    const drawer = document.querySelector('[data-drawer]');
    if (!drawer) return;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.body.classList.add('menu-open');
    document.querySelector('[data-drawer-open]')?.setAttribute('aria-expanded', 'true');
    drawer.querySelector('button, a')?.focus();
  }
  function closeDrawer() {
    const drawer = document.querySelector('[data-drawer]');
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('menu-open');
    const opener = document.querySelector('[data-drawer-open]');
    opener?.setAttribute('aria-expanded', 'false');
    setTimeout(() => { drawer.hidden = true; }, 400);
    opener?.focus();
  }

  /* ---------- 6. Karussell (Rezensionen, Produkte) ---------- */
  function initCarousels(root = document) {
    root.querySelectorAll('[data-carousel]').forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = '1';
      const track = wrap.querySelector('[data-carousel-track]');
      const prev = wrap.querySelector('[data-carousel-prev]');
      const next = wrap.querySelector('[data-carousel-next]');
      if (!track) return;
      const step = () => (track.firstElementChild?.getBoundingClientRect().width || 300) + 24;
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      const update = () => {
        if (prev) prev.disabled = track.scrollLeft < 5;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5;
      };
      track.addEventListener('scroll', update, { passive: true });
      update();

      const autoplay = parseInt(wrap.dataset.autoplay, 10);
      if (autoplay && !reduceMotion) {
        let paused = false;
        wrap.addEventListener('mouseenter', () => { paused = true; });
        wrap.addEventListener('mouseleave', () => { paused = false; });
        wrap.addEventListener('focusin', () => { paused = true; });
        setInterval(() => {
          if (paused || document.hidden) return;
          if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 5) track.scrollTo({ left: 0, behavior: 'smooth' });
          else track.scrollBy({ left: step(), behavior: 'smooth' });
        }, autoplay * 1000);
      }
    });
  }

  /* ---------- 7. AJAX Warenkorb ---------- */
  function showToast(html) {
    let toast = document.querySelector('.toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.innerHTML = html;
    toast.classList.add('is-open');
    clearTimeout(toast._t);
    toast._t = setTimeout(() => toast.classList.remove('is-open'), 4200);
  }

  async function updateCartCount() {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
        el.classList.remove('bump'); void el.offsetWidth; el.classList.add('bump');
      });
    } catch (e) { /* noop */ }
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ajax-cart]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const label = btn?.innerHTML;
    if (btn) { btn.disabled = true; btn.innerHTML = btn.dataset.loading || '…'; }
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message);
      await updateCartCount();
      showToast(`<span>✓ <strong>${data.product_title}</strong> ${form.dataset.addedText || 'wurde in den Warenkorb gelegt.'}</span> <a href="${window.Shopify?.routes?.root || '/'}cart">${form.dataset.cartText || 'Zum Warenkorb'}</a>`);
    } catch (err) {
      showToast(`<span>${err.message || 'Fehler'}</span>`);
    } finally {
      if (btn) { btn.disabled = false; btn.innerHTML = label; }
    }
  });

  /* ---------- 8. Produktseite: Variante, Menge, Galerie ---------- */
  function initProduct(root = document) {
    root.querySelectorAll('[data-product]').forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = '1';
      const json = wrap.querySelector('[data-product-json]');
      const variants = json ? JSON.parse(json.textContent) : [];
      const idInput = wrap.querySelector('[name="id"]');
      const priceEl = wrap.querySelector('[data-price]');
      const btn = wrap.querySelector('[data-add-button]');
      const fmt = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'de', { style: 'currency', currency: wrap.dataset.currency || 'EUR' }).format(cents / 100);

      const onChange = () => {
        const selected = [...wrap.querySelectorAll('[data-option]')].map((s) => (s.type === 'radio' ? (s.checked ? s.value : null) : s.value)).filter((v) => v !== null);
        const variant = variants.find((v) => v.options.every((o, i) => o === selected[i]));
        if (!variant) { if (btn) { btn.disabled = true; btn.textContent = btn.dataset.unavailable; } return; }
        idInput.value = variant.id;
        if (priceEl) {
          priceEl.innerHTML = variant.compare_at_price > variant.price
            ? `<span class="price__current">${fmt(variant.price)}</span> <s>${fmt(variant.compare_at_price)}</s>`
            : `<span class="price__current">${fmt(variant.price)}</span>`;
        }
        if (btn) { btn.disabled = !variant.available; btn.textContent = variant.available ? btn.dataset.add : btn.dataset.soldout; }
        if (variant.featured_media) {
          const thumb = wrap.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          thumb?.click();
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };
      wrap.querySelectorAll('[data-option]').forEach((s) => s.addEventListener('change', onChange));

      wrap.querySelectorAll('[data-qty]').forEach((q) => {
        const input = q.querySelector('input');
        q.querySelector('[data-qty-minus]')?.addEventListener('click', () => { input.value = Math.max(1, (+input.value || 1) - 1); });
        q.querySelector('[data-qty-plus]')?.addEventListener('click', () => { input.value = (+input.value || 1) + 1; });
      });

      const main = wrap.querySelector('[data-gallery-main]');
      wrap.querySelectorAll('[data-gallery-thumb]').forEach((t) => {
        t.addEventListener('click', () => {
          wrap.querySelectorAll('[data-gallery-thumb]').forEach((x) => x.setAttribute('aria-current', 'false'));
          t.setAttribute('aria-current', 'true');
          const img = main?.querySelector('img');
          if (img) {
            img.style.opacity = 0;
            setTimeout(() => {
              img.src = t.dataset.full; img.srcset = t.dataset.srcset || ''; img.alt = t.dataset.alt || '';
              img.style.opacity = 1;
            }, 180);
          }
        });
      });
    });
  }

  /* ---------- 9. Zähler (Count-up) ---------- */
  function initCounters(root = document) {
    const els = root.querySelectorAll('[data-count]');
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseFloat(el.dataset.count);
        const decimals = (el.dataset.count.split('.')[1] || '').length;
        const start = performance.now();
        const dur = reduceMotion ? 0 : 1600;
        const tick = (now) => {
          const p = dur ? Math.min((now - start) / dur, 1) : 1;
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = (target * eased).toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.5 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Init ---------- */
  function initAll(root = document) {
    initReveal(root);
    initMegaMenu(document);
    initCarousels(root);
    initProduct(root);
    initCounters(root);
    initMarquees();
    collectScrollEls();
    onScrollFrame();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  // Theme-Editor: Sections neu initialisieren
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
